import feathersClient from '../../../apiClient';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import useSearchString from '../../../hooks/UrlParamsHandler/useSearchParams';
import queryClient from '../../../queryClient';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import Modal from 'react-modal';
import { FavouriteIcon } from '@tymbe/ui-icons';
import styles from '../dev/shift-detail/Modal.module.css';

export const DeleteShiftModal = ({
  isOpen,
  toggle,
}: {
  isOpen: boolean;
  toggle: () => void;
}) => {
  const [searchParams, setSearchParams] = useSearchString();
  const shiftId = searchParams.get('shiftId') || undefined;

  const { mutateAsync: deleteShift } = useMutation(
    ['delete-shift'],
    async (removeShiftId: number) =>
      feathersClient.service('shift').remove(removeShiftId),
    {
      onSuccess: () => {
        SuccessAlert('Směna smazána');
      },
      onError: () => {
        ErrorAlert('Něco se pokazilo :(');
      },
    }
  );

  const onShiftRemoved = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('shiftId');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const onDelete = async (removeShiftId: number) => {
    try {
      await deleteShift(removeShiftId);
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      toggle();
      onShiftRemoved();
    } catch (e: unknown) {
      if (
        e &&
        typeof e === 'object' &&
        Object.hasOwn(e, 'code') &&
        e.code === 403
      ) {
        ErrorAlert(
          'Není možno smazat objednávku, která již proběhla nebo probíhá a je na ní někdo přihlášen.'
        );
      } else {
        ErrorAlert('Objednávku není možné smazat');
      }
      toggle();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggle}
      contentLabel="Delete shift modal"
      className="bg-bg m-auto rounded-xl px-6 py-4"
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <div className="flex justify-between">
        <h2 className="text-fg-900 text-[16px] leading-[24px] font-semibold mb-4">
          Opravdu chcete smazat směnu?
        </h2>
        <button
          className="p-0"
          type="button"
          aria-label="close button"
          onClick={toggle}
        >
          <FavouriteIcon name="close" className="size-5 text-secondary-600" />
        </button>
      </div>
      <div className="text-fg-900 text-sm mb-4">
        Tato akce je nevratná a směna bude navždy smázána.
      </div>
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          aria-label="close button"
          className="px-4 py-2 border border-secondary-300 font-semibold text-sm text-secondary-600"
          onClick={toggle}
        >
          Zrušit
        </button>
        <button
          type="button"
          aria-label="delete button"
          className="px-4 py-2 border border-[#EF4444] font-semibold text-sm text-bg bg-[#EF4444]"
          onClick={() => onDelete(Number.parseInt(shiftId!))}
        >
          Smazat
        </button>
      </div>
    </Modal>
  );
};
